<script setup>
import { useAcademyStore, useListStore } from "@/stores";
import { computed, onMounted } from "vue";
import courseStatuses from "@/config/lists/courseStatuses";

const academyStore = useAcademyStore();
const listStore = useListStore();

const keyword = computed({
  get: () => academyStore.filters.keyword,
  set: (value) => (academyStore.filters.keyword = value),
});

const advanced = computed({
  get: () => academyStore.filters.advanced,
  set: (value) => (academyStore.filters.advanced = value),
});

const appliedFilters = computed(() => {
  const adv = advanced.value;
  return Object.values(adv).filter((val) => {
    if (val == null) return false; // null or undefined
    if (Array.isArray(val)) return val.length > 0; // empty array
    if (typeof val === "string") return val.trim() !== ""; // empty string
    return true; // all other non-null, non-empty values
  }).length;
});

const types = computed(() => listStore.courseTypes);
const sections = computed(() => listStore.courseSections);
const areas = computed(() => listStore.thematicAreas);
const programs = computed(() => listStore.programs);
const branches = computed(() => listStore.branches);

const handleResetBtnClick = () => {
  academyStore.resetAction();
};

const hideSimpleSearch = computed(() => {
  return false;
});

const variant = "academy";

onMounted(async () => {
  await listStore.fetchCourseTypes();
  await listStore.fetchCourseSections();
  await listStore.fetchThematicAreas();
  await listStore.fetchBranches();
  await listStore.fetchPrograms();
});
</script>

<template>
  <div v-if="!hideSimpleSearch">
    <v-text-field
      v-model="advanced.cycle"
      name="searchbar"
      label="Search (by cycle)"
    />
  </div>
  <v-expansion-panels class="mb-5">
    <v-expansion-panel :class="`advanced-filters-panel panel-${variant}`">
      <v-expansion-panel-title :class="`title-${variant}`">
        <v-icon icon="mdi-filter" /> <b>Advanced Filters</b>
        <div class="ml-2 text-caption font-weight-bold">
          ({{ appliedFilters }}
          <span v-if="appliedFilters == 1">filter</span>
          <span v-else>filters</span> applied)
        </div>
      </v-expansion-panel-title>
      <v-expansion-panel-text>
        <div class="d-flex ga-5 filter-row">
          <div class="flex-1-0-0">
            <v-text-field
              v-model="advanced.cycle"
              name="cycle"
              label="Training Cycle (ID)"
            />
          </div>

          <div class="flex-1-0-0">
            <v-autocomplete
              v-model="advanced.sections"
              label="Training Course"
              :items="sections"
              item-title="name"
              item-value="id"
              variant="outlined"
              multiple
              clearable
            />
          </div>

          <div class="flex-1-0-0">
            <v-select
              v-model="advanced.thematicAreas"
              label="Thematic Area"
              :items="areas"
              item-title="name"
              item-value="id"
              variant="outlined"
              multiple
              clearable
            />
          </div>
          <div class="flex-1-0-0">
            <v-select
              v-model="advanced.types"
              label="Course Type"
              :items="types"
              item-title="name"
              item-value="id"
              variant="outlined"
              multiple
              clearable
            />
          </div>
          <div class="flex-1-0-0">
            <v-select
              v-model="advanced.status"
              label="Status"
              :items="courseStatuses"
              item-title="name"
              item-value="value"
              variant="outlined"
              multiple
              clearable
            />
          </div>
        </div>
        <div class="d-flex ga-5 filter-row">
          <div class="flex-1-0-0">
            <v-date-input
              v-model="advanced.regDates"
              label="Registration Dates"
              variant="outlined"
              prepend-icon=""
              multiple="range"
              placeholder="DD/MM/YYYY"
              prepend-inner-icon="$calendar"
              clearable
            />
          </div>
          <div class="flex-1-0-0">
            <v-date-input
              v-model="advanced.startDates"
              label="Start Dates"
              variant="outlined"
              prepend-icon=""
              multiple="range"
              placeholder="DD/MM/YYYY"
              prepend-inner-icon="$calendar"
              clearable
            />
          </div>
          <div class="flex-1-0-0">
            <v-date-input
              v-model="advanced.endDates"
              label="End Dates"
              variant="outlined"
              prepend-icon=""
              multiple="range"
              placeholder="DD/MM/YYYY"
              prepend-inner-icon="$calendar"
              clearable
            />
          </div>
          <div class="flex-1-0-0">
            <v-select
              v-model="advanced.branch"
              label="Odyssea Branch"
              :items="branches"
              item-title="name"
              item-value="id"
              variant="outlined"
              clearable
            />
          </div>
          <div class="flex-1-0-0">
            <v-select
              v-model="advanced.projects"
              label="Odyssea Project"
              :items="programs"
              item-title="name"
              item-value="id"
              variant="outlined"
              multiple
              clearable
            />
          </div>
        </div>
        <div class="d-flex ga-5">
          <v-btn
            :color="variant"
            variant="outlined"
            @click="handleResetBtnClick"
          >
            Reset
          </v-btn>
        </div>
      </v-expansion-panel-text>
    </v-expansion-panel>
  </v-expansion-panels>
</template>

<style scoped>
.filter-row {
  flex-wrap: wrap !important;
}

@media (max-width: 599px) {
  .filter-row > div {
    flex: 0 0 100% !important;
  }
}

@media (min-width: 600px) and (max-width: 959px) {
  .filter-row > div {
    flex: 0 0 calc(50% - 10px) !important;
  }
}
</style>
